import { THEMES } from '@dfl/mui-react-common';
import { grey } from '@mui/material/colors';
import { createTheme, lighten, Theme } from '@mui/material';
import { components } from './components';
import themeConfig from './branding';

export const RED = '#F84842';
export const GREEN = '#65BE46';
export const GRAY = '#646D82';
export const BLUE = '#37AEFF';
export const WARNING = '#FDA20A';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
    formLabel: string;
    link: string;
  }

  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
  }
}

// Extend color prop on components
declare module '@mui/material/Chip' {
  export interface ChipPropsColorOverrides {
    opacity: true;
  }
}
// #fdfdfd59

export const common = createTheme({
  components: {
    ...components,
    MuiAvatar: {
      styleOverrides: {
        root: {
          '.MuiAvatar-img': {
            objectPosition: 'top',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          background: palette.mode === 'light' ? lighten(palette.primary.light, 0.8) : grey['800'],

          '.MuiTableCell-root,.MuiTableSortLabel-root': {
            // color: theme.palette.primary.main,
          },
        }),
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '&.row-warning': {
            background: `${theme.palette.warning.main}30`,
          },
          '&.row-late': {
            background: `${theme.palette.error.main}25`,
          },
          '&.row-severe': {
            background: `${theme.palette.error.main}30`,
          },
          '&.row-critical': {
            background: `${theme.palette.error.main}35`,
          },
          '&.row-error': {
            background: `${theme.palette.error.main}30`,
          },
          '&.row-success': {
            background: `${theme.palette.success.main}30`,
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          '.MuiButtonBase-root': {
            minWidth: 20,
            textTransform: 'none',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        ':root, :before, :after': {
          '--primary-color': `${theme.palette.primary.main} !important`,
        },
        '.truncate': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '.phone-input-large, .phone-input-middle': {
          '.react-tel-input': {
            'input.form-control': {
              padding: '16px 14px 16px 58px!important',
            },
          },
        },
        '.phone-input-small': {
          '.react-tel-input': {
            'input.form-control': {
              padding: '9px 14px 7px 58px!important',
            },
          },
        },
        '.react-tel-input': {
          '.special-label': {
            fontSize: '11px!important',
          },
        },
        // ApexCharts dark mode styles
        '.apexcharts-canvas': {
          '.apexcharts-text': {
            fill: theme.palette.text.primary,
          },
          '.apexcharts-xaxis-label, .apexcharts-yaxis-label': {
            fill: theme.palette.text.secondary,
          },
          '.apexcharts-gridline': {
            stroke: theme.palette.mode === 'light' ? '#e0e0e0' : 'rgba(255, 255, 255, 0.1)',
          },
          '.apexcharts-tooltip': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            border: `1px solid ${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
            '.apexcharts-tooltip-title': {
              backgroundColor: `${theme.palette.mode === 'light' ? grey[100] : grey[800]} !important`,
              borderBottom: `1px solid ${theme.palette.divider} !important`,
              color: `${theme.palette.text.primary} !important`,
            },
            '.apexcharts-tooltip-text-y-label, .apexcharts-tooltip-text-y-value': {
              color: `${theme.palette.text.primary} !important`,
            },
          },
          '.apexcharts-legend-text': {
            color: `${theme.palette.text.primary} !important`,
          },
          '.apexcharts-datalabel-label': {
            fill: theme.palette.text.primary,
          },
          '.apexcharts-datalabel-value': {
            fill: theme.palette.text.primary,
          },
        },
      }),
    },
  },
  typography: {
    h1: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: 1.6,
      letterSpacing: '0.0075em',
    },
    h2: {
      fontSize: 18,
      fontWeight: 500,
    },
    h3: {
      fontSize: 15,
      fontWeight: 500,
    },
    h4: {
      fontSize: 16,
      fontWeight: 500,
    },
    body1: {
      fontSize: 14,
    },
    h6: {
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.5,
      whiteSpace: 'normal',
      textTransform: 'none',
    },
    subtitle1: {
      fontSize: 16,
      fontWeight: 300,
      textTransform: 'none',
    },
  },
});

const LIGHT = {
  ...common,
  palette: {
    mode: THEMES.LIGHT,
    sidebar: {
      background: '#fff',
      color: 'secondary.main',
      activeColor: 'secondary.main',
      active: 'primary.light',
    },
    ...themeConfig.theme,
  },
};

const DARK = {
  ...common,
  palette: {
    mode: THEMES.DARK,
    sidebar: '#0F1621',
    spaceSelector: '#1C242D',
    background: {
      default: '#161D24',
      paper: '#1C242D',
    },
    divider: '#47515D',
    primary: {
      main: '#7BB2DA',
      light: '#2E353E',
      contrastText: '#fff',
    },
    opacity: {
      main: 'rgba(255,255,255,0.35)',
      contrastText: 'rgba(255,255,255,0.87)',
    },
    secondary: {
      ...grey,
      main: grey['500'],
      light: grey['700'],
    },
    neutral: {
      main: grey['700'],
    },
    error: {
      main: '#B63A3A',
    },
    formLabel: 'rgba(255,255,255,0.75)',
    link: '#78A4EB',
  },
};

export const THEME_SETTING = {
  [THEMES.LIGHT]: LIGHT,
  [THEMES.DARK]: DARK,
};
